import type { FunctionalUseQueryOptions } from 'app/types/network';
import { http } from '../lib/axios';
import { useUser } from './user';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CreatePlanDTO, PaymentSheet, Plan } from 'app/types/plan';
import type { CreatePaymentSheet } from 'app/types/payment';
import { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { bffUserProfileLoadedAtom, isLoggedInAtom, userPlanAtom } from 'app/atoms';
import { addMonths, isBefore, isInLastWeek } from 'app/lib/date';
import { PlanRecurrenceEnum } from 'app/schemas/plan';
import { useAllGroups } from 'app/network/groups';
import { useAllAddresses } from 'app/network/address';
import { planKeys } from './keys';
import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { useEffect } from 'react';

export const DEFAULT_PLAN_CODE = 'REGULAR';
export const LEGACY_TRIAL_PLAN_CODE = 'FREETRIAL';
export const FREE_TRIAL_CREDIT_PLAN_CODE = 'CREDITMEMBER';
export const DEFAULT_TRIAL_PLAN_CODE = FREE_TRIAL_CREDIT_PLAN_CODE;
export const OFFRE_DEFRICHEURS_PLAN_CODE = 'OFFRE-DEFRICHEURS';
export const REFERRAL_PRIZE_PLAN_CODE = 'REFERRAL-PRIZE-5';
export const OFFRE_TRIAL_PLAN_CODE = 'TRIAL-OFFER-5';

export const isFreeTrialPlanCode = (code?: string | null) =>
  code === LEGACY_TRIAL_PLAN_CODE || code === FREE_TRIAL_CREDIT_PLAN_CODE;

export function useCreateOnePlan(opts: UseMutationOptions<Plan, Error, CreatePlanDTO> = {}) {
  const { data: user, refetch: refetchUser } = useUser();
  const { refetch: refetchGroups } = useAllGroups();
  const { refetch: refetchActivePlans } = useMyActivePlans();
  const { refetch: refetchAddresses } = useAllAddresses();
  return useMutation({
    mutationFn: (payload) => http.post<Plan>('/plans', { ...payload, userId: user?.id }).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      await refetchUser();
      await refetchActivePlans();
      await refetchGroups();
      await refetchAddresses();
      opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useStopPlanRecurrence(opts: UseMutationOptions<Plan, Error, Plan['id']> = {}) {
  const { refetch: refetchActivePlans } = useMyActivePlans();
  return useMutation({
    mutationFn: (planId) =>
      http.post<Plan>(`/plans/${planId}/stop`).then(async (res) => {
        await refetchActivePlans();
        return res.data;
      }),
    ...opts,
  });
}

export function useCreatePaymentSheet(opts: UseMutationOptions<PaymentSheet, number, CreatePaymentSheet> = {}) {
  return useMutation<PaymentSheet, number, CreatePaymentSheet>({
    mutationFn: (payload) =>
      http
        .post<PaymentSheet>('/transactions/payment-sheet', {
          recurrence: payload.recurrence,
          source: payload.source,
          coupon: payload.coupon || undefined,
        })
        .catch((err: unknown) => {
          if (err instanceof AxiosError) {
            throw ((err as AxiosError).response?.data as any)?.statusCode;
          }
          throw err;
        })
        .then((res) => {
          return res.data;
        }),
    ...opts,
  });
}

export function useMyActivePlans(opts: FunctionalUseQueryOptions<Plan[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [, setUserPlan] = useAtom(userPlanAtom);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const queryKey = planKeys.list('all');
  const query = useQuery<Plan[]>({
    queryKey,
    queryFn: async () => (await http.get<Plan[]>('/plans')).data,
    staleTime: 5 * 60 * 1000,
    ...opts,
    enabled: isLoggedIn && bffUserProfileLoaded && (opts.enabled ?? true),
  });

  // The BFF can populate this query directly, without running queryFn.
  useEffect(() => {
    if (query.data) setUserPlan(query.data);
  }, [query.data, setUserPlan]);

  return query;
}

export function shouldShowHelpCompensationSubscribe(userInsured: boolean | undefined, activePlan?: Plan): boolean {
  if (isWhiteLabel(WhiteLabel.Mutuo)) return false;

  if (!userInsured) return true;
  if (!activePlan) return true;
  if (isFreeTrialPlanCode(activePlan.code.code)) return true;

  return askForPayment(activePlan);
}

export function askForPayment(activePlan?: Plan): boolean {
  if (!activePlan) return false;
  // Show payment button if user is on free trial, or if their plans will expire in less than 2 months, but only if the user is not a Corp user
  const isOnFreeTrial = isFreeTrialPlanCode(activePlan.code.code);
  const planExpired = !!activePlan.expiresAt && isBefore(new Date(activePlan.expiresAt), new Date());
  const planExpiresInLessThanOneMonths =
    !!activePlan.expiresAt && isBefore(new Date(activePlan.expiresAt), addMonths(new Date(), 1));
  const isPlanRecurring =
    activePlan.recurrence === PlanRecurrenceEnum.monthly || activePlan.recurrence === PlanRecurrenceEnum.yearly;
  return (isOnFreeTrial || planExpiresInLessThanOneMonths) && (planExpired || !isPlanRecurring);
}

export const signedUpRecently = (plan: Plan | undefined) => plan?.code.code && isInLastWeek(new Date(plan.createdAt));
